import { useCallback, memo } from 'react';
import {
  AG1,
  B1,
  Grid,
  PromptDialog,
  Typography,
  TypographyWithTooltip,
  useSnackbar,
  useTranslations,
} from '@uniqkey-frontend/shared-app';
import { TrustedActionType } from '@uniqkey-backend-organization-web/api-client';
import { logException } from '../../../../../services/sentryService';
import { getActiveOrganizationId } from '../../../../../services/organizationService';
import { useTrustedPortalStore } from '../../../../../modules/TrustedPortalModule/store';
import useTrustedActionsAPI from '../../../../../hooks/useTrustedActionsAPI';

const TOOLTIP_PROPS = { cursorPointer: true };

interface ITrustedPortalToggleProps {
  onClick: () => Promise<void>;
  isModalOpen: boolean;
  onModalClose: () => void;
}

const TrustedPortalToggle = (props: ITrustedPortalToggleProps) => {
  const { onClick, isModalOpen, onModalClose } = props;
  const activeOrganizationId = getActiveOrganizationId();
  const isTrustedPortalEnabled = useTrustedPortalStore.useIsEnabledByOrganizationId()[
    activeOrganizationId!
  ] ?? false;
  const { t } = useTranslations();
  const { showSuccess, showError } = useSnackbar();
  const { deleteTrustedAction } = useTrustedActionsAPI();

  const handleDeactivate = useCallback(async () => {
    try {
      await deleteTrustedAction(TrustedActionType.TrustedPortal);
      onModalClose();
      showSuccess({ text: t('trustedPortalToggle.trustPortalDeactivated') });
    } catch (e) {
      logException(e, { message: 'TrustedPortalToggle/handleDeactivate exception' });
      showError({
        text: t('common.somethingWentWrong'),
      });
    }
  }, [deleteTrustedAction, showSuccess, showError, t, onModalClose]);

  return (
    <>
      <TypographyWithTooltip
        variant="subtitle1"
        title={isTrustedPortalEnabled
          ? t('trustedPortalToggle.clickToDeactivate')
          : t('trustedPortalToggle.clickToActivate')}
        backgroundColor={isTrustedPortalEnabled ? AG1 : B1}
        borderRadius={2}
        padding="0px 4px"
        tooltipProps={TOOLTIP_PROPS}
        onClick={onClick}
      >
        {isTrustedPortalEnabled ? t('trustedPortalToggle.on') : t('trustedPortalToggle.off')}
      </TypographyWithTooltip>
      {isModalOpen && (
        <PromptDialog
          open={isModalOpen}
          onClose={onModalClose}
          onSubmit={handleDeactivate}
          title={t('deactivateModal.title')}
          approvalButtonText={t('deactivateModal.deactivate')}
          rejectionButtonText={t('common.cancel')}
          approvalButtonColor="primary"
        >
          <Grid container rowGap={2}>
            <Grid item>
              <Typography>
                {t('deactivateModal.description.paragraph1')}
              </Typography>
            </Grid>
            <Grid item>
              <Typography>
                {t('deactivateModal.description.paragraph2')}
              </Typography>
            </Grid>
            <Grid item>
              <Typography>
                {t('deactivateModal.description.paragraph3')}
              </Typography>
            </Grid>
          </Grid>
        </PromptDialog>
      )}
    </>
  );
};

export default memo(TrustedPortalToggle);
